import { IDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { DirectionalHint } from '@fluentui/react';
import { IRadioOption } from 'shell-models-lib';
import { writeBehaviorValue } from 'app/model/pipeline/copysink/format-write-settings/delta-format-write-settings';
import { getAppendOption, getOverwriteOption, getUpsertOption } from '../consts/copy-job-const';
import { cdcDestination } from '../consts/metadata-consts';
import { deltaFullLinkedServiceTypes } from 'app/authoring/copy-job/wizard/copy-job-delta.const';
import { GlobalVariables } from 'app/services/globals';

const deltaWriteBehavior: Array<IRadioOption<writeBehaviorValue>> = [
    {
        label: stringResources.WriteBehavior_Option_Insert,
        value: 'Insert'
    },
    {
        label: stringResources.Overwrite,
        value: 'Overwrite'
    },
    {
        label: stringResources.WriteBehavior_Option_Upsert,
        value: 'Upsert'
    }
]

export const deltaMetadata: IDatasetTypeMetadata = {
    type: adfModelTypeMap.delta.datasetType,
    displayName: stringResources.ModelName_DelaFormatDataset,
    /**
     * The Delta format is only offered as a dataset of its own where full Delta support ships; a clone-tier copy
     * job builds its own format list, so it does not go through here.
     */
    supportLinkedServiceTypes: GlobalVariables.supportDelta ? deltaFullLinkedServiceTypes : [],
    isSource: true,
    isSink: true,
    supportGetMetadataActivity: true,
    datasetKind: 'file',
    icon: {
        className: 'svg-i-20x20-delta-lake'
    },
    isFirstClass: true,
    category: ['File'],
    isV2FormatDataset: true,
    formatReadSettingControls: [
        {
            type: 'parameterizable',
            child: {
                type: 'date-time-picker',
                bind: 'timestampAsOf',
                placeholder: 'YYYY-MM-DDThh:mm:ss',
                description: stringResources.timestampAsOfDesc,
                label: stringResources.DataFlowColumnType_Timestamp,
                drops: DirectionalHint.topLeftEdge,
                styleOverrides: {
                    width: '496px' // label 215 + input 264 + info 17
                }
            },
            dynamicContentTextClass: 'input-spaced'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'versionAsOf',
                description: stringResources.versionAsOfDesc,
                label: stringResources.VersionLabel,
                placeholder: '0, 1, 2...',
                inputType: 'number',
                min: 0
            },
            dynamicContentTextClass: 'input-spaced'
        }
    ],
    formatWriteSettingControls: [
        {
            type: 'parameterizable',
            child: {
                type: 'checkbox',
                bind: 'applyVOrder',
                label: stringResources.Dataset_applyVOrder,
                description: stringResources.Dataset_applyVOrder_Description,
                escapeDescription: false,
                labelPosition: 'left',
                defaultValue: true,
                useDefaultValueWhenValueIsNull: true
            },
            dynamicContentTextClass: 'input-spaced'
        },
        {
            type: 'radio-switch',
            label: stringResources.ModelName_WriteBehavior,
            bind: 'writeBehavior',
            isParameterizable: true,
            outsideAdvancedSectionInFabric: true,
            options: deltaWriteBehavior,
            defaultValue: 'Insert',
            cases: [
                {
                    caseValue: 'Upsert',
                    children: [
                        {
                            type: 'sql-upsert',
                            bind: {
                                keys: 'upsertKeys'
                            },
                            hasTempDB: false,
                            hasInterimSchemaName: false,
                            keyColumnsAttributeName: 'upsertKeys',
                            parameterizable: true,
                            required: true
                        }
                    ]
                }
            ]
        },
        {
            type: 'checkbox-switch',
            bind: 'enablePartitionOption',
            hide: `{ writeBehavior !== 'Overwrite' }`,
            showDynamically: true,
            isParameterizable: true,
            label: stringResources.ModelName_Enable_Sink_Partition,
            description: stringResources.CopySinkEnablePatitionOptionDescription,
            parameterizedLabel: stringResources.ModelName_Partition_Option,
            defaultValue: false,
            labelPosition: 'left',
            cases: [
                {
                    caseValue: true,
                    children: [
                        {
                            type: 'store-setting-partition-keys-order',
                            bind: 'partitionNameList',
                            description: stringResources.CopySinkPatitionColumnNamesDescription,
                            actionType: 'Overwrite'
                        }
                    ]
                }
            ]
        }
    ],
    /**
     * A Delta source is always read through its change data feed
     */
    cdc: {
        supportCDC: true,
        supportedCDCDestinations: cdcDestination,
        notSupportTableDetection: true,
        supportGenericCdc: false
    },
    copyJob: {
        updateMethod: {
            supportUpdateMethod: true,
            options: [getAppendOption(), getOverwriteOption(), getUpsertOption()],
            supportUpsertInBatch: true,
            updateMethodProperties: ['writeBehavior', 'upsertSettings'],
            defaultUpdateMethod: 'Upsert',
            upsertKeyColumnRequired: true
        }
    }
};
